// Config file for useAutoRefetchSelector - Maps state paths to action creators
import {
  getAllCameraAndFovsDevices,
  getAllCompanionDevices,
  fetchAllSites,
  fetchCVApp,
  //refreshSession,
  getAllEvents,
} from '../store/actions';

/**
 * State-to-Action mapping configuration
 * Maps Redux state paths to their corresponding action creators
 * Used by useAutoRefetchSelector to automatically dispatch actions when state is empty
 */
export const stateToActionMapping = {
  // Session management
  'sessionReducer.activeSession': null,//refreshSession,
  
  // Device management
  'deviceReducer.companionDeviceList': getAllCompanionDevices,
  'deviceReducer.cameraList': getAllCameraAndFovsDevices,
  'deviceReducer.fovList': getAllCameraAndFovsDevices,
  'deviceReducer.roiList': null, // No auto-action available
  'deviceReducer.ccdUpdateList': null, // No auto-action available
  'deviceReducer.refreshCameraStatus': getAllCameraAndFovsDevices,
  
  // Sites management
  'sitesReducer': fetchAllSites,
  'sitesReducer.siteTagList': fetchAllSites,
  
  // CV Apps management
  'cvAppReducer': fetchCVApp,
  'cvAppReducer.cvAppList': fetchCVApp,
  
  // Events management
  'eventReducer.allEvents': getAllEvents,
  'eventReducer.criticalEvents': getAllEvents,
  'eventReducer.sourceId': getAllEvents,
  'eventReducer.evidence': getAllEvents,
  
  // Statistics management
  'statisticsReducer.widgets': null, // No auto-action available
  
  // Images management
  'imagesReducer.streamImages': null, // No auto-action available
  'imagesReducer.floorImages': null, // No auto-action available
  
  // Other reducers without auto-actions
  'eventSettingsReducer': null,
  'eventPageNumberReducer': null,
  'availableIncidentsReducer': null,
};

/**
 * Get action creator for a given state path
 * @param {string} statePath - The dot-notation path to the state (e.g., 'deviceReducer.companionDeviceList')
 * @returns {Function|null} - The action creator function or null if no action available
 */
export const getActionForStatePath = (statePath) => {
  return stateToActionMapping[statePath] || null;
};

/**
 * Check if a state path has an auto-action configured
 * @param {string} statePath - The dot-notation path to the state
 * @returns {boolean} - True if an action is configured for this state path
 */
export const hasAutoAction = (statePath) => {
  return statePath in stateToActionMapping && stateToActionMapping[statePath] !== null;
};

/**
 * Get the state path from a selector function
 * This is a utility to extract state path from selector for debugging
 * @param {Function} selector - The selector function
 * @returns {string} - Extracted state path or 'unknown'
 */
export const getStatePathFromSelector = (selector) => {
  try {
    // Create a mock state object to trace the selector path
    const mockState = new Proxy({}, {
      get(target, prop) {
        return new Proxy({}, {
          get(target, subProp) {
            // Return the path when accessed
            if (subProp === 'toString') {
              return () => `${prop}.${subProp}`;
            }
            return `${prop}.${subProp}`;
          }
        });
      }
    });
    
    const result = selector(mockState);
    return typeof result === 'string' ? result : 'unknown';
  } catch (error) {
    return 'unknown';
  }
};

/**
 * Default empty state checker
 * Determines if a value is considered "empty" and needs refetching
 * @param {any} value - The value to check
 * @returns {boolean} - True if the value is considered empty
 */
export const isEmptyState = (value) => {
  // Null or undefined
  if (value === null || value === undefined) {
    return true;
  }
  
  // Empty arrays
  if (Array.isArray(value) && value.length === 0) {
    return true;
  }
  
  // Empty objects (but not Date objects or other special objects)
  if (typeof value === 'object' && 
      value.constructor === Object && 
      Object.keys(value).length === 0) {
    return true;
  }
  
  // Empty strings
  if (typeof value === 'string' && value.trim() === '') {
    return true;
  }
  
  // For nested objects, check common patterns
  if (typeof value === 'object' && value !== null) {
    // Check for objects with empty arrays in common properties
    if (value.sites && Array.isArray(value.sites) && value.sites.length === 0) {
      return true;
    }
    
    if (value.cvAppList && Array.isArray(value.cvAppList) && value.cvAppList.length === 0) {
      return true;
    }
    
    // Check for session objects
    if (value.activeSession && !value.activeSession.token) {
      return true;
    }
  }
  
  return false;
};

export default {
  stateToActionMapping,
  getActionForStatePath,
  hasAutoAction,
  getStatePathFromSelector,
  isEmptyState,
};